import { DISMISS_STATE_KIND } from './DISMISS_STATE_KIND';
import { FOURTEEN_DAYS_MS } from './FOURTEEN_DAYS_MS';
import { getLocalState } from './getLocalState';
import { GHOST_ICONS_RENDER_MODE } from './GHOST_ICONS_RENDER_MODE';
import type { GhostIconsRenderMode } from './GhostIconsRenderMode';
import { reportUnexpectedDismissState } from './reportUnexpectedDismissState';
import { THIRTY_DAYS_MS } from './THIRTY_DAYS_MS';

/**
 * Persistent dismiss-state for 3P ghost icons.
 *
 * Transitions (grouped by trigger; "—" means the call is a no-op):
 *
 *   recordFirstView()        — first render of the icons
 *     null          → SEEN
 *     SEEN          → —      (idempotent: keeps original firstViewedAt)
 *     TIMER_RUNNING → —
 *     DISMISSED     → —
 *
 *   recordSourcesClicked()   — user opened the Sources Dropdown
 *     null          → TIMER_RUNNING   (firstViewedAt = sourcesClickedAt = now)
 *     SEEN          → TIMER_RUNNING   (only if SEEN window has not expired)
 *     SEEN expired  → —      (>= 30 days old; do not re-surface hidden icons)
 *     TIMER_RUNNING → —      (keeps original sourcesClickedAt)
 *     DISMISSED     → —      (final state)
 *
 *   recordDismissed()        — user clicked Connect
 *     null          → DISMISSED       (firstViewedAt = dismissedAt = now)
 *     SEEN          → DISMISSED
 *     TIMER_RUNNING → DISMISSED       (preserves sourcesClickedAt)
 *     DISMISSED     → —      (keeps original dismissedAt)
 *
 * UI behavior uses a single render-mode query:
 * - getGhostIconsRenderMode(aaid): whether icons are hidden, visible+animated,
 *   or visible+static
 *
 * Render matrix by state:
 * - null: visible + animated (first exposure)
 * - SEEN: visible + static while < 30 days since firstViewedAt, else hidden
 * - TIMER_RUNNING: visible + static while < 14 days since sourcesClickedAt, else hidden
 * - DISMISSED: hidden
 *
 * Expiry is COMPUTED, never PERSISTED:
 * The 14-day (TIMER_RUNNING) and 30-day (SEEN) windows are evaluated lazily on
 * every getGhostIconsRenderMode() read by comparing Date.now() against the stored
 * timestamp. When a window lapses, NOTHING rewrites the stored state — the entry
 * stays SEEN / TIMER_RUNNING in localStorage indefinitely, and only the derived
 * render mode flips to HIDDEN. There is no timer, effect, cron, or cleanup job
 * that transitions an expired SEEN / TIMER_RUNNING state into DISMISSED. The only
 * way to reach the DISMISSED state is recordDismissed() (user clicks Connect).
 *
 * DISMISSED (state) vs HIDDEN (render mode) — do not conflate the two:
 * - DISMISSED is a persisted, terminal STATE KIND in localStorage. It is reached
 *   only via recordDismissed() and is the only one-way/permanent state.
 * - HIDDEN is a RENDER-MODE output computed at read time. Multiple inputs map to
 *   it: DISMISSED (always), TIMER_RUNNING (>= 14 days), SEEN (>= 30 days), or a
 *   missing aaid (guarded by the caller). So "hidden on screen" does NOT imply
 *   the stored state is DISMISSED.
 *
 * Consequence: expiry-based hiding is a pure function of Date.now() vs the stored
 * timestamp, so it is NOT monotonic — rolling the clock back (or restoring an old
 * backup) would make an expired SEEN / TIMER_RUNNING entry render again. Only
 * DISMISSED hides permanently regardless of the clock.
 *
 * Fail-open behavior:
 * Any storage read/parse/validation failure is treated as null so the nudge still shows.
 */
export const getGhostIconsRenderMode = (aaid: string): GhostIconsRenderMode => {
	const state = getLocalState(aaid);
	if (state === null) {
		// No valid saved state means we treat this as first exposure, so icons should be shown
		// with first-time animation.
		return GHOST_ICONS_RENDER_MODE.ANIMATED;
	}

	switch (state.kind) {
		case DISMISS_STATE_KIND.DISMISSED:
			// User clicked Connect earlier (recordDismissed), so keep ghost icons hidden from now on.
			return GHOST_ICONS_RENDER_MODE.HIDDEN;

		case DISMISS_STATE_KIND.TIMER_RUNNING:
			// After the Sources Dropdown is opened, keep icons visible (static, not animated) for 14 days.
			// Past 14 days we just return HIDDEN here — this is a pure read, so the stored state stays
			// TIMER_RUNNING forever; it is never rewritten to DISMISSED. The icons simply stop rendering.
			return Date.now() - state.sourcesClickedAt < FOURTEEN_DAYS_MS
				? GHOST_ICONS_RENDER_MODE.STATIC
				: GHOST_ICONS_RENDER_MODE.HIDDEN;

		case DISMISS_STATE_KIND.SEEN:
			// This branch is the SEEN state: user has not opened the Sources Dropdown yet.
			// Keep icons visible (static, not animated) for up to 30 days.
			// Past 30 days we just return HIDDEN here — same as TIMER_RUNNING above, this is a pure read,
			// so the stored state stays SEEN forever; it is never rewritten to DISMISSED.
			return Date.now() - state.firstViewedAt < THIRTY_DAYS_MS
				? GHOST_ICONS_RENDER_MODE.STATIC
				: GHOST_ICONS_RENDER_MODE.HIDDEN;

		default:
			// Fail open if an unexpected kind reaches here.
			reportUnexpectedDismissState({
				functionName: 'getGhostIconsRenderMode',
				kind: (state as { kind?: string }).kind,
			});
			return GHOST_ICONS_RENDER_MODE.ANIMATED;
	}
};
